import { useSelector } from "react-redux";
import { useMemo, useState } from "react";

import EChartsReact from "echarts-for-react";

import { Container, Table, TableBody, Typography } from "@mui/material";

import HoverPaper from "../HoverPaper";

import Colours from "../../../helpers/colours";
import NoDataRow from "../../NoDataRow";
import { fns } from "../../../helpers/common";
import { DISPLAY_ELEMENT_HELPER } from "../../../helpers/filters";

export const REPORT_PIE_CHART = "REPORT_PIE_CHART";
export const ReportPieChart = ({
    js,
    data,
    entriesLimit = 4,
    metric = "bookingRooms",
    title = "",
    total = false,
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const { chartData, totalActual } = useMemo(() => {
        if (!data || Object.keys(data).length === 0) {
            return { chartData: [], totalActual: 0 };
        }
        const rows = Object.keys(data)
            .filter(
                (country) =>
                    Object.keys(data[country]?.result || {}).length > 0,
            )
            .map((country) => ({
                name: country,
                value: data[country]?.result?.[metric] ?? 0,
            }));

        const filtered = rows.filter((e) => e.value > 0);

        filtered.sort((a, b) => b.value - a.value);

        // Top N + Others
        const top = filtered.slice(0, entriesLimit);
        const rest = filtered.slice(entriesLimit);

        if (rest.length > 0) {
            const othersValue = rest.reduce((sum, e) => sum + e.value, 0);
            top.push({
                name: "Others",
                value: othersValue,
            });
        }

        const totalActual = top.reduce((sum, e) => sum + e.value, 0);
        return { chartData: top, totalActual };
    }, [data, metric, entriesLimit]);

    const option = useMemo(() => {
        return {
            tooltip: {
                trigger: "item",
                formatter: ({ name, value, percent }) => {
                    const pct = Math.round(percent);
                    return `
                    <strong>${name}</strong><br/>
                    Total <strong>${fns(
                        hotelID,
                        value,
                        DISPLAY_ELEMENT_HELPER[metric].format ?? {},
                        false,
                    )}</strong><br/>
                    Percentage <strong>${pct}%</strong>
                `;
                },
            },

            legend: {
                bottom: 0,
                left: "center",
                orient: "horizontal",
                icon: "circle",
                itemWidth: 10,
                itemHeight: 10,
                textStyle: { fontSize: 12 },
                formatter: (name) => {
                    const item = chartData.find((e) => e.name === name);
                    if (!item || totalActual === 0) return name;
                    const pct = Math.round((item.value / totalActual) * 100);
                    return `${name} ${pct}%`;
                },
            },

            series: [
                {
                    name: metric,
                    type: "pie",
                    radius: "70%",
                    center: ["50%", "45%"],
                    avoidLabelOverlap: false,

                    label: {
                        show: true,
                        formatter: (params) => {
                            const pct = Math.round(params.percent);
                            return `{name|${params.name}}: {percent|${pct}%}`;
                        },
                        rich: {
                            name: {
                                fontWeight: "bold",
                                fontSize: 12,
                                color: "#333",
                            },
                            percent: {
                                fontSize: 12,
                                color: "#333",
                            },
                        },
                    },

                    labelLine: {
                        show: true,
                        length: 12,
                        length2: 8,
                    },

                    itemStyle: {
                        borderColor: "#fff",
                        borderWidth: 2,
                    },

                    data: chartData,
                },
            ],
        };
    }, [chartData, totalActual, metric]);

    return (
        <>
            <Typography fontWeight={"bold"} sx={{ pt: 2 }}>
                {title}
            </Typography>

            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                {chartData.length > 0 ? (
                    <>
                        {total && (
                            <Typography>
                                Total:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {fns(
                                        hotelID,
                                        totalActual,
                                        DISPLAY_ELEMENT_HELPER[metric].format ??
                                            {},
                                        false,
                                    )}
                                </span>
                            </Typography>
                        )}
                        <EChartsReact option={option} />
                    </>
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text="No geographic data available for this selection" />
                        </TableBody>
                    </Table>
                )}
            </Container>
        </>
    );
};

export default ReportPieChart;
